import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { googleLogout, useGoogleLogin } from "@react-oauth/google";
import { Modal } from "react-bootstrap";
import { storeUserData } from "./utils/auth";
import { Spinner } from "react-bootstrap";
import CryptoJS from "crypto-js";
import authService from "./utils/authService";
import ReCAPTCHA from "react-google-recaptcha";
import "./Login.css";

interface UserProfile {
  name: string;
  email: string;
  picture?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setisLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [recaptchaVerified, setRecaptchaVerified] = useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');

  // reCAPTCHA v2 callback functions
  const onRecaptchaChange = (token: string | null): void => {
    if (token) {
      setRecaptchaToken(token);
      setRecaptchaVerified(true);
    } else {
      setRecaptchaToken('');
      setRecaptchaVerified(false);
    }
  };

  const onRecaptchaExpired = (): void => {
    setRecaptchaToken('');
    setRecaptchaVerified(false);
  };

  const onRecaptchaError = (): void => {
    setRecaptchaToken('');
    setRecaptchaVerified(false);
    setAlertMessage('reCAPTCHA verification failed. Please try again.');
    setShowAlert(true);
  };

  // Reset reCAPTCHA
  const resetRecaptcha = (): void => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setRecaptchaToken('');
    setRecaptchaVerified(false);
  };

  const handleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: (codeResponse) => {
      if (!recaptchaVerified) {
        setAlertMessage("Please complete the reCAPTCHA verification.");
        setShowAlert(true);
        return;
      }
      setUser(codeResponse);
    },
    onError: (error) => {
      console.error("Google login failed:", error);
      setAlertMessage("Google login failed. Please try again.");
      setShowAlert(true);
      setisLoading(false);
    },
    scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/meetings.space.created https://www.googleapis.com/auth/meetings.space.readonly https://www.googleapis.com/auth/drive.metadata.readonly'
  });

  useEffect(() => {
    if (!user) return;

    const sendAuthCodeToBackend = async () => {
      try {
        setisLoading(true);
        
        // First API call - login-with-google (provides all user data and access token)
        const loginResponse = await authService.post("userauth/login-with-google/", {
            code: user.code
        });

        if (loginResponse.status !== 200) {
          throw new Error("Failed to authenticate user with login-with-google");
        }

        const loginData = loginResponse.data;

        // Extract user data from login-with-google response
        const { email, user_name, user_picture, access_token, id_token } = loginData;
        
        let people_id = "";
        if (id_token && id_token.sub) {
          people_id = id_token.sub;
        }
        

        // Second API call - suite/login (using data from login-with-google)
        const suiteResponse = await authService.post("/suite/login/", {
          email: CryptoJS.enc.Utf8.parse(email).toString(),
          access_token: CryptoJS.enc.Utf8.parse(access_token).toString()
        }, { timeout: 15000 });

        const suiteData = suiteResponse.data;

        // Use suite/login data for user authentication (contains JWT access_token)
        const data = suiteData;

        if (data.access_token) {
          // Store JWT access token for all API calls
          sessionStorage.setItem("access_token", data.access_token);
          
          // Store user data in sessionStorage from login-with-google response
          sessionStorage.setItem("Name", loginData.user_name);
          sessionStorage.setItem("Email", loginData.email);
          sessionStorage.setItem("UserId", loginData.Userid);
          sessionStorage.setItem("UserPicture", loginData.user_picture);
          
          // Update local state
          
          // Store encrypted user data for other API calls
          storeUserData({
            Type: JSON.parse(loginData.user_cat),
            exists: loginData.exists,
            access: [], // Add appropriate access array if needed
            Name: loginData.user_name,
            Id: loginData.Userid,
            access_token: data.access_token
          });

          authService.post('/suite/update-suite-user-people-id', {
            user_id: loginData.Userid,
            people_id: people_id
          })

          const userType = JSON.parse(loginData.user_cat);
          if (userType && userType.length === 1) {
            if (userType[0] === "AD") {
              navigate("/admin/courses");
              sessionStorage.setItem("Admin", "True");
            } else if (userType[0] === "TR") {
              navigate("/trainer/reports");
              sessionStorage.setItem("Trainer", "True");
            } else if (userType[0] === "CO") {
              navigate("/coordinator/filter-page");
              sessionStorage.setItem("Coordinator", "True");
            } else if (userType[0] === "SC") {
              navigate("/coordinator/filter-page");
              sessionStorage.setItem("Coordinator", "True");
              sessionStorage.setItem("SC", "True");
            } else if (userType[0] === "PO") {
              navigate("/placement-officer/company");
              sessionStorage.setItem("PO", "True");
            }
            
          } else {
            navigate("/dashboard", { state: { data: userType } });
          }
        } else {
          setShowUpdateModal(true);
          setModalNewMessage("Authentication failed. Please login again.");
        }
      } catch (error: any) {
        setisLoading(false);
        console.error("Error sending auth code to backend:", error);
        
        // Reset reCAPTCHA on error
        resetRecaptcha();
        
        // Handle different types of errors
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          setAlertMessage("Request timed out. Please check your internet connection and try again.");
        } else if (error.response?.status === 401) {
          setAlertMessage("Authentication failed. Please try logging in again.");
        } else if (error.response?.status === 403) {
          setAlertMessage("Access denied. Please check your Google account permissions.");
        } else if (error.response?.status >= 500) {
          setAlertMessage("Server error. Please try again later.");
        } else {
          setAlertMessage("Login failed. Please try again.");
        }
        
        setShowAlert(true);
      }
    };

    sendAuthCodeToBackend();
  }, [user, navigate]);

  return (
    <div className="login container-fluid h-100 d-flex align-items-center justify-content-center">
      <div className="row w-100 justify-content-center">
        <div className="col-12 col-lg-8 d-flex align-items-center justify-content-center">
          <div className="">
            <span
              className="block text-start fw-bolder"
              style={{ color: "#fff", fontSize: "60px" }}
            >
              Exskilence
            </span>
            <p
              className="ps-5 text-start mb-3"
              style={{
                background: "linear-gradient(to right, #f5d547, #48e28f)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: "bold",
                fontStyle: "italic",
                fontSize: "24px",
              }}
            >
              <i>...Because Opportunity is a Right</i>
            </p>
            <div
              style={{ textAlign: "left", maxWidth: "600px", margin: "0 auto" }}
            >
              <p
                style={{
                  fontSize: "18px",
                  lineHeight: "1.8",
                  marginBottom: "1.5rem",
                  color: "#fff",
                }}
              >
                We are a Social Enterprise focused on providing Skilling and
                Placement Assistance to undergraduate students, from Computer
                Science Engineering and related streams, particularly from Tier
                2 and Tier 3 colleges.
              </p>
              <p
                style={{
                  fontSize: "18px",
                  lineHeight: "1.8",
                  color: "#fff",
                }}
              >
                At Exskilence, we are on a mission to empower individuals,
                transform careers, and unlock potential. We believe that quality
                tech education and real-world experience should be within
                everyone's reach, regardless of their background.
              </p>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-4 d-flex justify-content-center align-items-center px-3 py-4">
          <div
            className="card"
            style={{
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "100%",
            }}
          >
            <div className="card-body p-4 d-flex flex-column align-items-center justify-content-center">
              <h4
                className="text-center mb-4"
                style={{ color: "#333", fontWeight: "600" }}
              >
                Login with your Google account
              </h4>
              <div className="text-center w-100">
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner color="#6f42c1" size="sm" className="me-2" />{" "}
                    Signing in...
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-center">
                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                      <div className="mb-3 d-flex justify-content-center">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                          onChange={onRecaptchaChange}
                          onExpired={onRecaptchaExpired}
                          onErrored={onRecaptchaError}
                        />
                      </div>
                    ) : (
                      <div className="mb-3 p-3 border border-warning rounded bg-warning bg-opacity-10">
                        <small className="text-warning">
                          reCAPTCHA site key not configured.
                        </small>
                      </div>
                    )}
                    <button
                      onClick={() => handleLogin()}
                      disabled={process.env.REACT_APP_RECAPTCHA_SITE_KEY ? !recaptchaVerified : false}
                      className="btn w-100"
                      style={{
                        backgroundColor: (process.env.REACT_APP_RECAPTCHA_SITE_KEY ? recaptchaVerified : true) ? '#4168a3' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontWeight: '500',
                        fontSize: '16px',
                        cursor: (process.env.REACT_APP_RECAPTCHA_SITE_KEY ? recaptchaVerified : true) ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Sign in with Google
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalNewMessage}</Modal.Body>
      </Modal>
      <Modal show={showAlert} onHide={() => setShowAlert(false)} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header closeButton className='bg-primary'>
          <Modal.Title>Alert</Modal.Title>
        </Modal.Header>
        <Modal.Body>{alertMessage}</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={() => setShowAlert(false)}>Okay</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}